import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { XCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function SystemLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchSystemLogs();
  }, []);

  const fetchSystemLogs = async () => {
    const toastId = toast.loading('Loading system logs...');
    try {
      setLoading(true);
      const response = await apiClient.get('/api/v1/logs/');
      setLogs(response.data);
      toast.success('Logs loaded successfully', { id: toastId });
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error(`Failed to load logs: ${error.message}`, { id: toastId });
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      INFO: 'bg-blue-50 text-blue-700 border-blue-200',
      WARNING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      ERROR: 'bg-red-50 text-red-700 border-red-200',
      DEBUG: 'bg-gray-50 text-gray-700 border-gray-200',
    };
    return colors[level] || colors.INFO;
  };

  const getLevelBadgeColor = (level) => {
    const colors = {
      INFO: 'bg-blue-100 text-blue-800',
      WARNING: 'bg-yellow-100 text-yellow-800',
      ERROR: 'bg-red-100 text-red-800',
      DEBUG: 'bg-gray-100 text-gray-800',
    };
    return colors[level] || colors.INFO;
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (e) {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading system logs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Logs</h1>
          <p className="mt-2 text-sm text-gray-600">
            {logs.length} log entries • Real-time system activity and events
          </p>
        </div>
        <button
          onClick={fetchSystemLogs}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logs List */}
        <div className="lg:col-span-2 space-y-2">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {logs.length === 0 ? (
              <div className="text-center text-gray-500 py-12">
                No logs available
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    onClick={() => setSelectedLog(log)}
                    className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                      selectedLog === log ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${getLevelBadgeColor(log.level)}`}>
                            {log.level}
                          </span>
                          <span className="text-xs text-gray-500 font-mono">
                            {formatTimestamp(log.timestamp)}
                          </span>
                          <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                            {log.module}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {log.message}
                        </p>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 ml-2 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Log Details Panel */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg overflow-hidden sticky top-6">
            {selectedLog ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900">Log Details</h2>
                  <button
                    onClick={() => setSelectedLog(null)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Level */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Level</h3>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded ${getLevelBadgeColor(selectedLog.level)}`}>
                      {selectedLog.level}
                    </span>
                  </div>

                  {/* Timestamp */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Timestamp</h3>
                    <p className="text-sm text-gray-900 font-mono">
                      {formatTimestamp(selectedLog.timestamp)}
                    </p>
                  </div>

                  {/* Module */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Module</h3>
                    <p className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded">
                      {selectedLog.module}
                    </p>
                  </div>

                  {/* Message */}
                  <div>
                    <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Message</h3>
                    <div className={`p-4 rounded-lg border ${getLevelColor(selectedLog.level)}`}>
                      <p className="text-sm whitespace-pre-wrap break-words">
                        {selectedLog.message}
                      </p>
                    </div>
                  </div>

                  {/* Additional Data */}
                  {selectedLog.data && Object.keys(selectedLog.data).length > 0 && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Additional Data</h3>
                      <div className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                        <pre className="text-xs font-mono">
                          {JSON.stringify(selectedLog.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Stack Trace */}
                  {selectedLog.stack_trace && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Stack Trace</h3>
                      <div className="bg-red-900 text-red-100 p-4 rounded-lg overflow-x-auto">
                        <pre className="text-xs font-mono whitespace-pre-wrap">
                          {selectedLog.stack_trace}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Error Details */}
                  {selectedLog.error && (
                    <div>
                      <h3 className="text-xs font-medium text-gray-500 uppercase mb-1">Error Details</h3>
                      <div className="bg-red-50 border border-red-200 text-red-900 p-4 rounded-lg">
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {selectedLog.error}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">
                <p className="text-sm">Select a log entry to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
